import * as THREE from 'three';
import { puff, random } from '../../look/noise.js';
import { SpriteCloud } from '../sprites.js';
import type { WorldBuilder } from '../world.js';

const smooth = THREE.MathUtils.smoothstep;

/**
 * An erupting ash column (the volcano's, moved from island.ts): a thick dark column that climbs from
 * the crater, spreads into a drifting umbrella, and is lit orange from the lava beneath it by two
 * broad glows over the rim. How it climbs and spreads is the headset's; the settings place and size it.
 * `level` (0–1) is how strongly it erupts; `erupt(animate)` says whether it was thrown up just now
 * (it climbs from the crater) or has been there a while (it is whole at once).
 */
export function ashColumn(
  world: WorldBuilder,
  options: {
    at: THREE.Vector3;
    count: number;
    colour: THREE.ColorRepresentation;
    opacity: number;
    /** Seconds a puff takes to rise and fade. */
    seconds: number;
    /** Metres over the rim it starts, and how far it climbs. */
    base: number;
    top: number;
    /** Strength of the glows under it. */
    glow: number;
    glowColour: THREE.Color;
    seed: number;
    name: string;
  },
): {
  mesh: SpriteCloud['mesh'];
  update(dt: number, elapsed: number, level: number): void;
  erupt(animate: boolean): void;
} {
  const count = options.count;
  const next = random(options.seed);
  const ash = new SpriteCloud(count, {
    map: world.codeTexture(puff(64, 13)),
    colour: options.colour,
    opacity: options.opacity,
    name: options.name,
  });
  // Each puff a little lighter or darker than the next: billows, not a blur.
  const shade = Array.from({ length: count }, () => 0.7 + next() * 0.6);
  let strength = 0;
  // The column's underside, lit by the lava: two broad glows just over the rim.
  for (const [height, radius] of [
    [22, 26],
    [48, 34],
  ] as const) {
    const holder = new THREE.Object3D();
    holder.position.copy(options.at).add(new THREE.Vector3(0, height, 0));
    world.root.add(holder);
    world.addHalo({
      on: holder,
      centre: new THREE.Vector3(),
      radius,
      colour: options.glowColour.clone(),
      flicker: (t) => options.glow * strength * (0.85 + 0.15 * Math.sin(t * 2.3 + height)),
    });
  }
  // How long it has erupted: the column climbs from the crater, not fading in whole.
  let erupting = 999;
  const p = new THREE.Vector3();
  const glow = new THREE.Color(4.2, 1.6, 0.45);
  const grey = new THREE.Color();
  const tint = new THREE.Color();
  return {
    mesh: ash.mesh,
    erupt(animate) {
      erupting = animate ? 0 : 999;
    },
    update(dt, elapsed, level) {
      strength = level;
      if (level > 0.3) erupting += dt;
      ash.mesh.visible = level > 0.01;
      if (!ash.mesh.visible) return;
      for (let i = 0; i < count; i++) {
        // Shot up fast, slowing as it climbs, then spreading into a drifting
        // umbrella: about 250 m over the rim, where a pupil on the shore can
        // still see it without craning.
        const u = (((elapsed / options.seconds + i / count) % 1) + 1) % 1;
        // Not yet thrown up if the eruption is younger than this puff.
        const born = Math.min(1, Math.max(0, (erupting - u * options.seconds) / 2));
        const rise = 1 - (1 - u) ** 1.7;
        const spread = smooth(u, 0.5, 1);
        const around = i * 2.39996;
        const out = 7 + 30 * rise + 130 * spread;
        p.copy(options.at);
        p.y += options.base + options.top * rise;
        p.x += Math.sin(around) * out + 50 * rise + 90 * spread;
        p.z += Math.cos(around) * out * 0.8 - 35 * rise - 40 * spread;
        // Lit orange from the lava beneath, dark above; the top a little lighter in the sun.
        const lit = shade[i]! * (1 + 0.35 * spread);
        tint.copy(glow).lerp(grey.setScalar(lit), smooth(u, 0.0, 0.16));
        const alpha =
          smooth(u, 0, 0.03) * (1 - smooth(u, 0.78, 1)) * Math.min(1, level * 1.4) * born;
        ash.set(i, p, 30 + 58 * rise + 62 * spread, alpha, i * 2.1 + u * 0.6, tint);
      }
      ash.commit();
    },
  };
}
